import React, { useState } from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useApi } from '@/providers/ApiContext';
import { useDocTypesDispatch } from '@/providers/DocTypesContext';
import { createDocumentType } from '@/actions/documentType';
import { SET_DOC_TYPES } from '@/providers/actionTypes';
import type { DocumentType } from '@/types';

interface AdminDocumentTypeFormProps {
  open: boolean;
  onClose: () => void;
  doc_types: DocumentType[];
}

function AdminDocumentTypeForm({
  open,
  onClose,
  doc_types,
}: AdminDocumentTypeFormProps): React.JSX.Element {
  const api = useApi();
  const dispatch = useDocTypesDispatch();
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    enable_manifests: false,
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'enable_manifests' ? checked : value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!api) return;
    try {
      const maxLevel = doc_types.reduce(
        (max, docType) => Math.max(max, docType.level || 0),
        0,
      );

      const newDocType = await createDocumentType(api, {
        ...formData,
        level: maxLevel + 1,
      });

      const updatedDocTypes = [...doc_types, newDocType];
      dispatch({ type: SET_DOC_TYPES, payload: updatedDocTypes });
      handleClose();
    } catch (error) {
      console.error('Error creating document type:', error);
    }
  };

  const handleClose = (): void => {
    setFormData({
      name: '',
      code: '',
      enable_manifests: false,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Add New Document Type</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              required
              fullWidth
              label='Name'
              name='name'
              value={formData.name}
              onChange={handleChange}
              margin='normal'
            />
            <TextField
              required
              fullWidth
              label='Code'
              name='code'
              value={formData.code}
              onChange={handleChange}
              margin='normal'
              helperText='Use letters and underscores only, no spaces or special characters (e.g., new_doc_type)'
            />
            <FormControlLabel
              control={
                <Checkbox
                  name='enable_manifests'
                  checked={formData.enable_manifests}
                  onChange={handleChange}
                />
              }
              label='Enable Manifests'
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type='submit' variant='contained' color='primary'>
            Add
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AdminDocumentTypeForm;

